/**
 * Builds a CSV error report from import error rows.
 *
 * Used by the media-import XLSX result phase's "Pobierz raport błędów" button
 * (GET /api/import/:id/errors) and the availability-import result phase's
 * unmatched-rows download (GET /api/availability-import/:id/errors) — both
 * return `ImportErrorRow[]`, so this module lives in `src/lib/` (shared
 * across `media-import` and `availability-import`) rather than
 * inside either feature, per the cross-feature import ban in
 * `docs/WEB_ARCHITECTURE.md`. Turns the rows into a CSV string with proper
 * escaping for download in the browser.
 */
import type { ImportErrorRow } from '@ooh/contracts';

/** Column order in the generated CSV; mirrors {@link ERROR_REPORT_COLUMNS}. */
const ERROR_REPORT_FIELDS = ['row', 'grwId', 'field', 'message', 'severity'] as const;

/**
 * Raw identifying-column fields (nested under `ImportErrorRow.identifiers`),
 * appended after the base columns only when the caller passes
 * `headers.identifiers` — i.e. only the availability-import unmatched-rows
 * download. Media-import's error report keeps the original 5-column shape.
 */
const IDENTIFIER_FIELDS = [
  'supplierNumber',
  'supplierName',
  'latitude',
  'longitude',
  'address',
  'city',
] as const;
type IdentifierField = (typeof IDENTIFIER_FIELDS)[number];

/** Localized header labels, passed in so this module stays i18n-free and testable. */
export interface ErrorReportHeaders {
  row: string;
  grwId: string;
  field: string;
  message: string;
  severity: string;
  /** Present only for the availability-import unmatched-rows download; adds the raw identifier columns. */
  identifiers?: Record<IdentifierField, string>;
}

/**
 * Neutralizes CSV/spreadsheet formula injection: cells whose first character is one
 * of `= + - @`, TAB, or CR are interpreted as formulas by Excel/Sheets. Prefix such a
 * value with a single quote so the spreadsheet treats it as literal text. Runs BEFORE
 * RFC-4180 quoting so the guard apostrophe is itself protected.
 */
function guardFormulaInjection(value: string): string {
  if (/^[=+\-@\t\r]/.test(value)) {
    return `'${value}`;
  }
  return value;
}

/**
 * Escapes a single CSV field: first neutralize formula injection, then apply RFC 4180
 * quoting — wrap in double quotes when the value contains a comma, double quote, or
 * newline, and double any embedded quotes.
 */
function escapeCsvField(value: string): string {
  const guarded = guardFormulaInjection(value);
  if (/[",\r\n]/.test(guarded)) {
    return `"${guarded.replace(/"/g, '""')}"`;
  }
  return guarded;
}

/**
 * Resolves an error row's `code` to a localized message using the same
 * `resolveValidationMsg` the on-screen validation table calls — only falls back to the raw
 * `message` when the row has no `code` at all; a `code` with no matching locale key resolves
 * however the caller's translator handles a missing key (i18next returns the key itself).
 */
export type ResolveErrorMessage = (
  code: string | undefined,
  message: string,
  params?: Record<string, string>,
) => string;

function cellValue(
  errorRow: ImportErrorRow,
  field: (typeof ERROR_REPORT_FIELDS)[number],
  resolveMessage?: ResolveErrorMessage,
): string {
  if (field === 'message') {
    return resolveMessage
      ? resolveMessage(errorRow.code, errorRow.message, errorRow.params)
      : errorRow.message;
  }
  // The availability-import worker never sets the top-level `grwId` field
  // (that's a media-import concept) — it only ever populates
  // `identifiers.grwId`. Fall back to it so a GRW-ID-identified unmatched
  // row still shows its GRW id instead of a blank cell.
  const raw = field === 'grwId' ? (errorRow.grwId ?? errorRow.identifiers?.grwId) : errorRow[field];
  if (raw === undefined || raw === null) return '';
  return String(raw);
}

function identifierCellValue(errorRow: ImportErrorRow, field: IdentifierField): string {
  const raw = errorRow.identifiers?.[field];
  if (raw === undefined || raw === null) return '';
  return String(raw);
}

/**
 * Builds the full CSV document (header row + one row per error) using CRLF line
 * endings. The header labels are provided by the caller (localized). Identifier
 * columns are appended only when `headers.identifiers` is present.
 */
export function buildErrorReportCsv(
  errors: ImportErrorRow[],
  headers: ErrorReportHeaders,
  resolveMessage?: ResolveErrorMessage,
): string {
  const identifierHeaders = headers.identifiers;
  const headerCells = [
    ...ERROR_REPORT_FIELDS.map((field) => headers[field]),
    ...(identifierHeaders ? IDENTIFIER_FIELDS.map((field) => identifierHeaders[field]) : []),
  ];
  const headerLine = headerCells.map(escapeCsvField).join(',');

  const rows = errors.map((errorRow) => {
    const cells = [
      ...ERROR_REPORT_FIELDS.map((field) => cellValue(errorRow, field, resolveMessage)),
      ...(identifierHeaders
        ? IDENTIFIER_FIELDS.map((field) => identifierCellValue(errorRow, field))
        : []),
    ];
    return cells.map(escapeCsvField).join(',');
  });

  return [headerLine, ...rows].join('\r\n');
}

/** Stable, human-readable download filename for a given import job. */
export function errorReportFilename(importJobId: number): string {
  return `raport-bledow-importu-${importJobId}.csv`;
}

/**
 * Builds the CSV report from the error rows and triggers a browser download via a
 * temporary anchor + object URL. A UTF-8 BOM is prepended so Excel renders Polish
 * characters correctly.
 */
export function triggerErrorReportDownload(
  importJobId: number,
  errors: ImportErrorRow[],
  headers: ErrorReportHeaders,
  resolveMessage?: ResolveErrorMessage,
): void {
  const csv = buildErrorReportCsv(errors, headers, resolveMessage);
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = errorReportFilename(importJobId);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
